import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ABOUT } from "../data/content";
import { AccentHeadline } from "../components/AccentHeadline";

export default function About() {
  return (
    <div data-testid="page-about">
      {/* Hero */}
      <section className="bg-[#14110E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> {ABOUT.eyebrow}
          </div>
          <h1 className="mt-6 font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-4xl">
            <AccentHeadline text={ABOUT.headline} accent={ABOUT.headlineAccent} />
          </h1>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
            <p className="lg:col-span-6 text-[#F5F2EB] leading-relaxed">{ABOUT.lead}</p>
            <p className="lg:col-span-6 text-[#F5F2EB] leading-relaxed">{ABOUT.supporting}</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-2">
            {ABOUT.focusTags.map((t) => (
              <span
                key={t}
                className="px-4 py-2 border border-[#4A3D30] text-[13px] text-white/90 tracking-wide"
                data-testid={`about-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> {ABOUT.whoWeAre.eyebrow}
            </div>
            <h2 className="mt-6 font-display text-3xl lg:text-[40px] leading-[1.05] text-[#14110E] font-semibold tracking-tight">
              {ABOUT.whoWeAre.title}
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[#4A3D30] leading-relaxed">
            {ABOUT.whoWeAre.body.map((p, i) => (
              <p key={i} data-testid={`about-whoweare-${i}`}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Platform steps */}
      <section className="bg-[#F4F1EA] border-y border-[#DED6C6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> Our Platform
          </div>
          <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight max-w-3xl">
            Built to identify opportunity and support growth
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {ABOUT.platform.map((s) => (
              <div key={s.num} className="bg-white border border-[#DED6C6] p-7" data-testid={`about-step-${s.num.slice(0,2)}`}>
                <div className="font-mono text-xs tracking-[0.22em] text-[#F26A1B]">{s.num}</div>
                <h3 className="mt-4 font-display text-xl text-[#14110E] font-semibold">{s.title}</h3>
                <p className="mt-3 text-sm text-[#4A3D30] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles + focus */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> How We Operate
            </div>
            <h2 className="mt-6 font-display text-3xl lg:text-4xl text-[#14110E] font-semibold tracking-tight">
              Principles designed for long-term value creation
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {ABOUT.principles.map((pr) => (
                <div key={pr.title} className="border-l-2 border-[#F26A1B] pl-5 py-2" data-testid={`principle-${pr.title.replace(/\s+/g,"-").toLowerCase()}`}>
                  <h4 className="font-display text-lg text-[#14110E] font-semibold">{pr.title}</h4>
                  <p className="mt-2 text-sm text-[#4A3D30] leading-relaxed">{pr.body}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-8 lg:border-l lg:border-[#DED6C6]">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> Core areas of focus
            </div>
            <h3 className="mt-6 font-display text-2xl text-[#14110E] font-semibold">Institutional experience applied to entrepreneurial opportunity</h3>
            <ul className="mt-8 divide-y divide-[#DED6C6] border-y border-[#DED6C6]">
              {ABOUT.focus.map((f, i) => (
                <li key={f} className="flex items-center gap-5 py-4 text-[#14110E]" data-testid={`focus-${i}`}>
                  <span className="font-mono text-xs text-[#A79E93] w-10">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-medium">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-[#14110E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
            <span className="rule-orange" /> Leadership
          </div>
          <h2 className="mt-6 font-display text-3xl lg:text-4xl font-semibold tracking-tight">
            Financial and operating experience for Trafalgar's next phase
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {ABOUT.leadership.map((l) => (
              <div key={l.name} className="border border-[#3A2E22] p-8" data-testid={`leader-${l.name.replace(/\s+/g,"-").toLowerCase()}`}>
                <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F26A1B]">{l.role}</div>
                <h3 className="mt-4 font-display text-2xl font-semibold">{l.name}</h3>
                <p className="mt-4 text-sm text-[#F5F2EB] leading-relaxed">{l.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-14">
            <Link
              to="/investors"
              className="inline-flex items-center gap-2 btn-gradient rounded-full px-6 py-3.5 text-sm font-semibold tracking-wide"
              data-testid="about-cta-investors"
            >
              Investor Relations <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
